import PropTypes from 'prop-types';
import React from 'react';
import { WhiteTick } from '../../../assets';
import './Stepper.css';

const BallState = {
  COMPLETE: 0,
  ACTIVE: 1,
  DEFAULT: 2
};

function StepperContainer({ children }) {
  return (
    <section className='stepper-container'>
      {children}
    </section>
  );
}

StepperContainer.propTypes = {
  children: PropTypes.node.isRequired
};

function ListContainer({ children }) {
  return (
    <section className='stepper-list-container'>
      {children}
    </section>
  );
}

ListContainer.propTypes = {
  children: PropTypes.node.isRequired
};

function Progress({ value }) {
  return (
    <hr
      className='progress-bar'
      style={{
        background: `linear-gradient(to right, #007C92 ${value}%, #A8ACB8 ${value}%)`
      }}
    />
  );
}

Progress.propTypes = {
  value: PropTypes.number.isRequired
};

function BallWrapper({ children }) {
  return (
    <div className='ball-wrapper'>
      {children}
    </div>
  );
}

BallWrapper.propTypes = {
  children: PropTypes.node.isRequired
};

const Spacer = () => (
  <div className='spacer' />
);

const Label = ({ $state, children }) => {
  let labelClass;
  switch ($state) {
    case BallState.ACTIVE:
      labelClass = 'label-active';
      break;
    case BallState.COMPLETE:
      labelClass = 'label-complete';
      break;
    default:
      labelClass = 'label-default';
      break;
  }

  return <div className={labelClass}>{children}</div>;
};

Label.propTypes = {
  $state: PropTypes.oneOf([BallState.ACTIVE, BallState.COMPLETE, BallState.DEFAULT]).isRequired,
  children: PropTypes.node.isRequired
};

const BallStep = ({ $active, $completed, $default, $size, children }) => {
  let ballStepClass;
  if ($active) { ballStepClass = 'ball-step-active'; }
  else if ($completed) { ballStepClass = 'ball-step-completed'; }
  else if ($default) { ballStepClass = 'ball-step-default'; }

  return (
    <div
      className={ballStepClass}
      style={{
        width: $size || '30px',
        height: $size || '30px'
      }}
    >
      {children}
    </div>
  );
};

BallStep.propTypes = {
  $active: PropTypes.bool,
  $completed: PropTypes.bool,
  $default: PropTypes.bool,
  $size: PropTypes.string,
  children: PropTypes.node.isRequired
};

const Ball = ({ state, index }) => {
  if (state === BallState.COMPLETE) {
    return (
      <BallStep $completed>
        <img src={WhiteTick} alt='' />
      </BallStep>
    );
  }

  if (state === BallState.ACTIVE) {
    return <BallStep $active>{index}</BallStep>;
  }

  return <BallStep $default $size='9px' />;
};

Ball.propTypes = {
  state: PropTypes.oneOf([BallState.ACTIVE, BallState.COMPLETE, BallState.DEFAULT]).isRequired,
  index: PropTypes.number.isRequired,
  step: PropTypes.shape({
    substeps: PropTypes.number
  }).isRequired
};

export const Stepper = ({
  steps,
  currentStep,
  hideProgress,
  currentSubStep = 1
}) => {
  const getStepState = React.useCallback(
    (step, index) => {
      switch (true) {
        case index < currentStep:
          return BallState.COMPLETE;

        case index === currentStep:
          return BallState.ACTIVE;

        case index === currentStep && currentSubStep / step.substeps >= 1:
          return BallState.COMPLETE;

        case index > currentStep:
          return BallState.DEFAULT;
      }

      return BallState.DEFAULT;
    },
    [currentStep, currentSubStep]
  );

  const getProgress = React.useCallback(
    (step, index) => {
      if (index < currentStep) {
        return 100;
      }

      if (index === currentStep) {
        return Math.round(50 + (currentSubStep / step.substeps) * 50);
      }

      return 1;
    },
    [currentStep, currentSubStep]
  );

  return (
    <StepperContainer>
      {!hideProgress && (
        <ListContainer>
          {steps?.map((step, index, arr) => {
            return (
              <React.Fragment key={`${step.label}-${index}-step`}>
                <BallWrapper>
                  <Ball state={getStepState(step, index)} index={index + 1} step={step} />
                </BallWrapper>
                {index !== arr.length - 1 && <Progress value={getProgress(step, index)} />}
              </React.Fragment>
            );
          })}
        </ListContainer>
      )}

      {
        !hideProgress && (
          <ListContainer>
            {steps?.map((step, index, arr) => (
              <React.Fragment key={`${step.label}-${index}-label`}>
                <Label $state={getStepState(step, index)}>{step.label}</Label>
                {index !== arr.length - 1 && <Spacer />}
              </React.Fragment>
            ))}
          </ListContainer>
        )
      }
    </StepperContainer >
  );
};

Stepper.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    substeps: PropTypes.number
  })).isRequired,
  currentStep: PropTypes.number.isRequired,
  hideProgress: PropTypes.bool,
  currentSubStep: PropTypes.number
};
